/*
 * Boston Proper × page-level tracking.
 *
 * Fires the page-load + session-context events Atlas pushes on every
 * pageview (gtm.liquid + ga4.js.liquid). Sits alongside bp-track-gtm.js;
 * this file handles the firing schedule, that file owns the helpers.
 *
 * Events fired on every page:
 *   Begin DataLayer    — GTM init anchor (matches Atlas gtm.liquid:196)
 *   page_loaded        — GA4-style page event (Atlas ga4.js.liquid:40)
 *   Landing            — first session pageview only (Atlas gtm.liquid:260)
 *   Log State          — customer + cart state snapshot (Atlas gtm.liquid:341)
 *   Homepage           — only on `/` (Atlas gtm.liquid:354)
 *   DataLayer Loaded   — sentinel fired LAST (Atlas AllPages.liquid:3)
 *
 * Reads from window.__customer_id__, __customer_type__,
 * __first_touch__, __user_hashed_data__ — all seeded in
 * snippets/bp-analytics-globals.liquid.
 */

(function () {
  'use strict';

  if (window.__bpTrackPageWired) return;
  window.__bpTrackPageWired = true;

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------

  function push(eventName, extra) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: eventName };
    if (extra) {
      Object.keys(extra).forEach(function (k) { payload[k] = extra[k]; });
    }
    window.dataLayer.push(payload);
    if (window.__DL__) window.__DL__.lastEvent = eventName;
  }

  function pageType() {
    var t = (window.__pageType__) || '';
    if (t) return _pretty(t);
    // Fallback URL-based detection — matches Atlas's URL pattern checks.
    var p = window.location.pathname;
    if (p === '/' || p === '')                 return 'Homepage';
    if (p.indexOf('/products/') === 0)         return 'Product Detail';
    if (p.indexOf('/collections/') === 0)      return 'Site Browse';
    if (p.indexOf('/search') === 0)            return 'Search Results';
    if (p.indexOf('/cart') === 0)              return 'Cart';
    if (p.indexOf('/blogs/') === 0)            return 'Blog';
    if (p.indexOf('/pages/') === 0)            return 'Page';
    if (p.indexOf('/account') === 0)           return 'Account';
    return 'Other';
  }

  function _pretty(template) {
    if (!template) return 'Other';
    if (template === 'index')                  return 'Homepage';
    if (template === 'product')                return 'Product Detail';
    if (template.indexOf('collection') === 0)  return 'Site Browse';
    if (template === 'search')                 return 'Search Results';
    if (template === 'cart')                   return 'Cart';
    if (template.indexOf('article') === 0)     return 'Blog';
    if (template.indexOf('blog')    === 0)     return 'Blog';
    if (template.indexOf('page')    === 0)     return 'Page';
    if (template.indexOf('customer') === 0)    return 'Account';
    return 'Other';
  }

  // event_location — the page-type label pushed on page_loaded, with the
  // analytics-team corrections applied. Kept separate from pageType() so
  // landing_page_type / Log State / Landing keep their existing labels;
  // only page_loaded.event_location gets the corrected values.
  //   /collections/*          → "Product Listing" (was "Site Browse")
  //   /pages/digital-catalog  → "Digital Catalog" (was "Page")
  //   /account*               → left as pageType() ("Account"), unconfirmed
  function eventLocation() {
    var p = window.location.pathname;
    var t = (window.__pageType__ || '').toLowerCase();
    if (p.indexOf('/pages/digital-catalog') === 0 || t.indexOf('page.digital-catalog') === 0) {
      return 'Digital Catalog';
    }
    if (p.indexOf('/collections/') === 0 || t.indexOf('collection') === 0) {
      return 'Product Listing';
    }
    return pageType();
  }

  function utmAid() {
    try {
      return new URL(window.location.href).searchParams.get('aid') || '';
    } catch (_) { return ''; }
  }

  // user_details — spec shape, built centrally in bp-analytics-globals
  // (localStorage gtm_user_data backup + client-side SHA-256). Read the
  // cached async result when ready, else the synchronous interim. Kept
  // identical to bp-track-gtm.js _userDetails() so page_loaded and the
  // ecommerce events push the same object.
  function userDetails() {
    if (window.__bp_user_details__) return window.__bp_user_details__;
    if (typeof window.bpUserDetailsSync === 'function') return window.bpUserDetailsSync();
    var cid = window.__customer_id__;
    return {
      log_state:                cid ? 'Logged In' : 'Logged Out',
      user_id:                  null,
      customer_id:              cid ? String(cid) : null,
      customer_type:            window.__customer_type__ || 'new',
      orders_count:             window.__orders_count__ != null ? Number(window.__orders_count__) : null,
      is_first_order:           null,
      enhanced_conversion_data: window.__enhanced_conversion_data__ || {},
      user_hashed_data:         window.__user_hashed_data__ || {}
    };
  }

  // -------------------------------------------------------------------
  // Firing schedule
  // -------------------------------------------------------------------

  function fireAll() {
    var pt = pageType();
    var ft = window.__first_touch__ || {};

    // 1. Begin DataLayer (init sentinel, matches Atlas)
    push('Begin DataLayer');

    // 2. page_loaded — Atlas shape (ga4.js.liquid:40-53): clear
    // page_details first, then push with user_details NESTED INSIDE
    // page_details and NO separate page_type key (event_location IS the
    // page type). The old sibling user_details + page_type keys made the
    // container's page-scope user variables resolve undefined.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ page_details: null });
    push('page_loaded', {
      page_details: {
        page_path:         window.location.pathname,
        page:              window.location.pathname + window.location.search,
        landing_page:      ft.page || window.location.pathname,
        landing_page_type: ft.template ? _pretty(ft.template) : pt,
        utm_aid:           utmAid(),
        event_location:    eventLocation(),
        timestamp:         String(Date.now()),
        event_id:          Date.now() + '-' + Math.floor(Math.random() * 1e9),
        user_details:      userDetails()
      }
    });

    // 3. Landing — only on first session pageview
    try {
      var SESSION_LANDED = 'bp_session_landed';
      if (!sessionStorage.getItem(SESSION_LANDED)) {
        push('Landing', { pageType: pt });
        sessionStorage.setItem(SESSION_LANDED, '1');
      }
    } catch (_) { /* ignore storage errors */ }

    // 4. Log State — fired on every pageview. Enriched toward Atlas's
    // gtm.liquid Log State payload (customerEmail / customerInfo /
    // currency) using the Liquid-seeded globals. (totalSpent isn't
    // available client-side, so it's omitted.)
    var ecd = window.__enhanced_conversion_data__ || {};
    push('Log State', {
      userId:        window.__customer_id__   || '',
      customerEmail: ecd.email || '',
      customerInfo: {
        firstName:   ecd.first_name  || '',
        lastName:    ecd.last_name   || '',
        address:     ecd.street      || '',
        city:        ecd.city        || '',
        province:    ecd.state       || '',
        zip:         ecd.zip_code    || '',
        country:     ecd.country     || '',
        phone:       ecd.phone_number || '',
        totalOrders: window.__orders_count__ || 0
      },
      logState:      window.__customer_id__   ? 'Logged In' : 'Logged Out',
      customerType:  window.__customer_type__ || 'new',
      currency:      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
      firstLog:      ft.timestamp || Date.now(),
      timestamp:     Date.now(),
      pageType:      pt
    });

    // 5. Homepage event — only on homepage path
    if (window.location.pathname === '/' || window.location.pathname === '') {
      push('Homepage', { pageType: 'Homepage' });
    }

    // 6. Blog event — only on article templates
    var ptl = (window.__pageType__ || '').toLowerCase();
    if (ptl.indexOf('article') === 0) {
      push('Blog', {
        pageType: 'Blog',
        title:    document.title || ''
      });
    }

    // 7. login event — Atlas sets localStorage.login_GA4='true' after
    // a successful login submit; this re-emits on the post-login redirect.
    try {
      if (localStorage.getItem('login_GA4') === 'true') {
        if (window.bpTrack && window.bpTrack.login) {
          window.bpTrack.login('email');
        }
        localStorage.removeItem('login_GA4');
      }
    } catch (_) { /* ignore */ }

    // 8. DataLayer Loaded — sentinel fires LAST so GTM triggers
    // gated on "all events ready" can fire reliably.
    push('DataLayer Loaded');
  }

  // -------------------------------------------------------------------
  // Run
  // -------------------------------------------------------------------

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fireAll, { once: true });
  } else {
    fireAll();
  }
})();
